import { createContext, FC, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';

import { io } from 'socket.io-client';
import { socketConfig as config } from './config';
import { SocketEvent } from './socket-event';

import {
  useLazyGetBankAccountsQuery,
  useLazyGetDepositInvoicesQuery,
  useLazyGetProfileQuery,
  useLazyGetProfilesQuery,
  useLazyGetProfileTransactionsQuery,
  useLazyGetProfileWalletsQuery,
  useLazyGetTransactionQuery,
  useLazyGetTransactionsQuery,
} from '../../api';
import { RouterPaths } from '../../router/paths';
import { Notification } from '../../api/controllers/notifications/type';
import { useAppActions } from '../../utils/hooks';
import { notificationsAction } from '../../store/notifications/notifications';
import { PaymentMethod, TransactionDirection, TransactionStatus } from '../../enums';

export type SocketContext = {
  isConnected: boolean;
  error: Error | null;
  disconnect: () => void;
};

const SocketContext = createContext<SocketContext | null>(null);
SocketContext.displayName = 'SocketContext';

const socket = io(process.env.REACT_APP_API_BASE_SOCKET_WS_URL ?? '', config);

export const SocketProvider: FC<{ children: ReactNode }> = (props) => {
  const { profileUid, transactionId } = useParams<'profileUid' | 'transactionId'>();
  const location = useLocation();

  // variables
  const [isConnected, setConnectStatus] = useState(socket.connected);
  const [error, setError] = useState<Error | null>(null);

  const { addNewNotifications } = useAppActions(notificationsAction);

  const isDepositInvoicesPage = location.pathname === RouterPaths.Invoices;
  const isTransactionsPage = location.pathname === RouterPaths.Transactions;
  const isWithdrawalsPage = location.pathname === RouterPaths.Withdrawals;

  // api
  const [getProfile] = useLazyGetProfileQuery();
  const [getProfiles, { originalArgs: profilesOriginalArgs }] = useLazyGetProfilesQuery();
  const [getProfileWallets] = useLazyGetProfileWalletsQuery();
  const [getProfileTransactions, { originalArgs: profileTransactionsOriginalArgs }] =
    useLazyGetProfileTransactionsQuery();
  const [getTransaction] = useLazyGetTransactionQuery();
  const [getTransactions, { originalArgs: transactionsOriginalArgs }] =
    useLazyGetTransactionsQuery();
  const [getDepositInvoices, { originalArgs: depositInvoicesOriginalArgs }] =
    useLazyGetDepositInvoicesQuery();
  const [getBankAccounts] = useLazyGetBankAccountsQuery();

  // socket message example
  const exampleOfSocketMessage = {
    id: '345',
    direction: 'OUT',
    amount: 1,
    fee: 0.01,
    ext_transaction_id: null,
    payment_method: 'BANK',
    status: 'PENDING',
    transfer_info: {},
    created_at: '2024-07-08T09:50:25.650Z',
    finished_at: null,
    row_start: '2024-07-08T09:50:25.650Z',
    validity: null,
    wallet_uid: '9a614358-c596-4a2a-a092-ddfa45c7a116',
  };

  useEffect(() => {
    if (!isConnected) {
      socket.connect();
    }
  }, [isConnected]);

  useEffect(() => {
    // common events
    socket.on('connect', () => {
      setConnectStatus(true);
    });
    socket.on('disconnect', () => {
      setConnectStatus(false);
      setError(null);
    });
    socket.on('error', (data: Error) => {
      setError(data);
    });

    // custom events, which exactly works in admin-app
    socket.on(SocketEvent.TRANSACTION_CREATED, (data: unknown) => {
      profileUid && getProfileWallets({ profileUid });
      profileUid &&
        getProfileTransactions(
          profileTransactionsOriginalArgs ?? { profileUid, params: { page: 1, page_size: 20 } }
        );

      isTransactionsPage &&
        getTransactions(transactionsOriginalArgs ?? { params: { page: 1, page_size: 20 } });
      isWithdrawalsPage &&
        getTransactions(
          transactionsOriginalArgs ?? {
            params: {
              page: 1,
              page_size: 20,
              direction: TransactionDirection.OUT,
              status: TransactionStatus.PENDING,
              payment_method: PaymentMethod.BANK,
            },
          }
        );

      isDepositInvoicesPage &&
        getDepositInvoices(depositInvoicesOriginalArgs ?? { params: { page: 1, page_size: 20 } });
    });

    socket.on(SocketEvent.TRANSACTION_FAILED, (data: unknown) => {
      profileUid && getProfileWallets({ profileUid });
      profileUid &&
        getProfileTransactions(
          profileTransactionsOriginalArgs ?? { profileUid, params: { page: 1, page_size: 20 } }
        );

      // @ts-ignore
      if (transactionId && transactionId === data?.id) {
        getTransaction({ transactionId });
      }

      isTransactionsPage &&
        getTransactions(transactionsOriginalArgs ?? { params: { page: 1, page_size: 20 } });
      isWithdrawalsPage &&
        getTransactions(
          transactionsOriginalArgs ?? {
            params: {
              page: 1,
              page_size: 20,
              direction: TransactionDirection.OUT,
              status: TransactionStatus.PENDING,
              payment_method: PaymentMethod.BANK,
            },
          }
        );

      isDepositInvoicesPage &&
        getDepositInvoices(depositInvoicesOriginalArgs ?? { params: { page: 1, page_size: 20 } });
    });

    socket.on(SocketEvent.TRANSACTION_FINISHED, (data: unknown) => {
      profileUid && getProfileWallets({ profileUid });
      profileUid &&
        getProfileTransactions(
          profileTransactionsOriginalArgs ?? { profileUid, params: { page: 1, page_size: 20 } }
        );

      // @ts-ignore
      if (transactionId && transactionId === data?.id) {
        getTransaction({ transactionId });
      }

      isTransactionsPage &&
        getTransactions(transactionsOriginalArgs ?? { params: { page: 1, page_size: 20 } });
      isWithdrawalsPage &&
        getTransactions(
          transactionsOriginalArgs ?? {
            params: {
              page: 1,
              page_size: 20,
              direction: TransactionDirection.OUT,
              status: TransactionStatus.PENDING,
              payment_method: PaymentMethod.BANK,
            },
          }
        );

      isDepositInvoicesPage &&
        getDepositInvoices(depositInvoicesOriginalArgs ?? { params: { page: 1, page_size: 20 } });
    });

    socket.on(SocketEvent.WALLET_CREATED, (data: unknown) => {
      // @ts-ignore
      if (profileUid && profileUid === data?.profile_uid) {
        getProfileWallets({ profileUid });
      }
    });

    // TODO: check Notification type
    socket.on(SocketEvent.BANK_ACCOUNT_CREATED, (data: Notification) => {
      profileUid && getBankAccounts({ profileUid });
    });

    // TODO: check Notification type
    socket.on(SocketEvent.PROFILE_CREATED, (data: Notification) => {
      getProfiles(profilesOriginalArgs ?? { params: { page: 1, page_size: 20 } });
    });

    // custom events, which maybe works in admin-app
    // TODO: check Notification type
    socket.on(SocketEvent.APPLICANT_REVIEWED, (data: Notification) => {
      profileUid && getProfile({ profileUid });
      getProfiles(profilesOriginalArgs ?? { params: { page: 1, page_size: 20 } });
    });

    socket.on(SocketEvent.NOTIFICATION, (data: Notification) => {
      addNewNotifications(data);
      enqueueSnackbar(data.message, { variant: 'info' });
    });

    // custom events, which doesn't work in admin-app
    socket.on(SocketEvent.WALLET_REQUEST_FAILED, (data: unknown) => {
      // getProfileWallets({ profileUid: profileUid! });
      // console.log(SocketEvent.WALLET_REQUEST_FAILED);
    });

    socket.on(SocketEvent.WALLET_REQUEST_CREATED, (data: unknown) => {
      // getProfileWallets({ profileUid: profileUid! });
      // console.log(SocketEvent.WALLET_REQUEST_CREATED);
    });

    socket.on(SocketEvent.BANK_ACCOUNT_STATUS_CHANGE, (data: unknown) => {
      // getBankAccounts({ profileUid: profileUid! });
      // console.log(SocketEvent.BANK_ACCOUNT_STATUS_CHANGE);
    });

    socket.on(SocketEvent.PROFILE_STATUS_CHANGE, (data: unknown) => {
      // getProfile({ profileUid: profileUid! });
      // getProfiles({ params: { page: 1, page_size: 20 } });
      // console.log(SocketEvent.PROFILE_STATUS_CHANGE);
    });

    return () => {
      socket.off('connect');
      socket.off('disconnect');
      socket.off('error');
      //
      socket.off(SocketEvent.WALLET_REQUEST_FAILED);
      socket.off(SocketEvent.WALLET_REQUEST_CREATED);
      socket.off(SocketEvent.WALLET_CREATED);
      socket.off(SocketEvent.BANK_ACCOUNT_STATUS_CHANGE);
      socket.off(SocketEvent.PROFILE_STATUS_CHANGE);
      socket.off(SocketEvent.TRANSACTION_CREATED);
      socket.off(SocketEvent.TRANSACTION_FAILED);
      socket.off(SocketEvent.TRANSACTION_FINISHED);
      socket.off(SocketEvent.APPLICANT_REVIEWED);
      socket.off(SocketEvent.BANK_ACCOUNT_CREATED);
      socket.off(SocketEvent.PROFILE_CREATED);
      socket.off(SocketEvent.NOTIFICATION);
    };
  }, []);

  // disconnect socket handler
  const disconnectSocket = useCallback(() => {
    if (!isConnected) return;
    socket.disconnect();
  }, [isConnected]);

  // context values
  const contextValues = {
    isConnected,
    error,
    disconnect: disconnectSocket,
  };

  return <SocketContext.Provider value={contextValues} {...props} />;
};

export const useSocket = () => {
  const context = useContext(SocketContext);

  if (!context) {
    throw new Error('useSocket hook must be used inside SocketProvider context');
  }

  return context;
};
